import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { useFormContext } from 'react-hook-form';
import { FilterFieldConfig } from 'shared/mui/Filter/config';
import { useFilterContext } from 'shared/mui/Filter/context/filterContext';
import {
  AutocompleteInput,
  AutocompleteMultiselectInput,
  EnumAutocompleteInput,
} from 'shared/react-admin/Inputs';
import { ReferenceArrayInput, ReferenceInput } from 'shared/react-admin/ReferenceInputs';
import { cleanEmpty } from 'utils/cleanEmpty';

export const useOrderConversionFilterFields = (): FilterFieldConfig[] => {
  const translate = useTypedTranslate();
  const { resetField, setValue, watch } = useFormContext();
  const { setAppliedFilter, appliedFilters } = useFilterContext();

  const merchantWatched = watch('merchant');
  const providerWatched = watch('provider');

  return [
    {
      name: 'merchant',
      label: translate('app.filter.merchant'),
      chipType: 'autocomplete',
      chipProps: {
        resource: 'merchants',
        onApply: () => {
          setAppliedFilter('provider', []);
          setAppliedFilter('gateway', []);

          resetField('provider', { keepDirty: true });
          resetField('gateway', { keepDirty: true });
        },
        onDelete: () => {
          resetField('provider', { keepDirty: true });
          resetField('gateway', { keepDirty: true });

          setAppliedFilter('provider', []);
          setAppliedFilter('gateway', []);
        },
        onReset: () => {
          setValue('provider', appliedFilters?.['provider']);
          setValue('gateway', appliedFilters?.['gateway']);

          setAppliedFilter('provider', appliedFilters?.['provider']);
          setAppliedFilter('gateway', appliedFilters?.['gateway']);
        },
      },
      input: (variant) => (
        <ReferenceInput perPage={100} reference="merchants" source="merchant">
          <AutocompleteInput
            filterToQuery={(searchText: string) => ({ name: searchText })}
            fullWidth
            label={translate('app.filter.merchant')}
            {...(variant === 'field' && {
              onChange: () => {
                setValue('provider', []);
                setValue('gateway', []);
              },
            })}
            optionText="name"
            variant="outlined"
          />
        </ReferenceInput>
      ),
    },
    {
      name: 'provider',
      label: translate('app.filter.providers'),
      chipType: 'multiselect',
      chipProps: {
        resource: 'providers',
        limitTags: 1,
        onApply: () => {
          setAppliedFilter('gateway', []);
          resetField('gateway', { keepDirty: true });
        },
        onDelete: () => {
          resetField('gateway', { keepDirty: true });
          setAppliedFilter('gateway', []);
        },
        onReset: () => {
          setValue('gateway', appliedFilters?.['gateway']);
          setAppliedFilter('gateway', appliedFilters?.['gateway']);
        },
      },
      input: (variant) => (
        <ReferenceArrayInput
          filter={cleanEmpty({ merchantAccounts: { merchants: merchantWatched } })}
          perPage={100}
          reference="providers"
          sort={{ field: 'name', order: 'ASC' }}
          source="provider"
        >
          <AutocompleteMultiselectInput
            filterToQuery={(searchText: string) => ({ name: searchText })}
            label={translate('app.filter.providers')}
            name="provider"
            {...(variant === 'field' && {
              onChange: () => {
                setValue('gateway', []);
              },
            })}
            optionText="name"
          />
        </ReferenceArrayInput>
      ),
    },
    {
      name: 'gateway',
      label: translate('app.filter.gateways'),
      chipType: 'multiselect',
      chipProps: {
        resource: 'gateways',
        limitTags: 1,
      },
      input: (
        <ReferenceArrayInput
          filter={cleanEmpty({
            merchantAccounts: { merchants: merchantWatched },
            provider: providerWatched,
          })}
          perPage={100}
          reference="gateways"
          source="gateway"
        >
          <AutocompleteMultiselectInput
            filterToQuery={(searchText: string) => ({ name: searchText })}
            label={translate('app.filter.gateways')}
            name="gateway"
            optionText="name"
          />
        </ReferenceArrayInput>
      ),
    },
    {
      name: 'direction',
      label: translate('app.filter.direction'),
      chipType: 'enumAutocomplete',
      chipProps: {
        resource: 'directions',
      },
      input: (
        <EnumAutocompleteInput
          filterToQuery={(searchText: string) => ({ description: searchText })}
          fullWidth
          label={translate('app.filter.direction')}
          name="direction"
          optionText="description"
          optionValue="value"
          resource="directions"
          source="direction"
          variant="outlined"
        />
      ),
    },
  ];
};
